// utils/emailValidation.ts

export interface EmailValidationResult {
    isValid: boolean
    error?: string
}

// Consolidated list of consumer/personal email domains
const CONSUMER_EMAIL_DOMAINS = [
    // Major consumer providers
    "gmail.com",
    "googlemail.com",
    "yahoo.com",
    "yahoo.co.uk",
    "ymail.com",
    "rocketmail.com",
    "hotmail.com",
    "hotmail.co.uk",
    "outlook.com",
    "live.com",
    "msn.com",
    "aol.com",
    "icloud.com",
    "me.com",
    "mac.com",

    // International providers
    "yandex.com",
    "mail.ru",
    "rediffmail.com",
    "free.fr",
    "gmx.de",
    "gmx.com",
    "web.de",
    "t-online.de",

    // Privacy-focused providers (personal use)
    //"protonmail.com",
    "tutanota.com",
    "hushmail.com",

    // Other popular personal providers
    "zoho.com",
    "mail.com",
    "fastmail.com"
]

// Disposable/temporary email domains
const DISPOSABLE_EMAIL_DOMAINS = [
    "gardsiir.com",
    "mailinator.com",
    "10minutemail.com",
    "guerrillamail.com",
    "tempmail.com",
    "temp-mail.org",
    "trashmail.com",
    "yopmail.com",
    "getnada.com",
    "dispostable.com",
    "sharklasers.com",
    "fakeinbox.com",
    "throwaway.email",
    "maildrop.cc",
    "tempail.com",
    "mailnesia.com",
    "emailondeck.com",
    "mohmal.com",
    "mytrashmail.com",
    "anonymbox.com"
]

// Blocked company domains (competitors, etc.)
const BLOCKED_COMPANY_DOMAINS = ["reducto", "pulse", "extend", "chunkr", "chonkie"]

/**
 * Validates if an email address is acceptable for business use
 * Blocks consumer emails, disposable emails, and specific company domains
 */
export function validateBusinessEmail(email: string): EmailValidationResult {
    // Basic validation
    if (!email || typeof email !== "string") {
        return { isValid: false, error: "Email is required" }
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
        return { isValid: false, error: "Please enter a valid email address" }
    }

    const domain = email.split("@")[1]?.toLowerCase()
    if (!domain) {
        return { isValid: false, error: "Invalid email format" }
    }

    // Check for consumer/personal email domains
    if (CONSUMER_EMAIL_DOMAINS.includes(domain)) {
        return {
            isValid: false,
            error: "Please use a business email address. Personal email providers are not allowed."
        }
    }

    // Check for disposable email domains
    if (DISPOSABLE_EMAIL_DOMAINS.includes(domain)) {
        return {
            isValid: false,
            error: "Temporary or disposable email addresses are not allowed. Please use a business email."
        }
    }

    // Check for blocked company domains (using includes for partial matches)
    if (BLOCKED_COMPANY_DOMAINS.some(blockedDomain => domain.includes(blockedDomain))) {
        return {
            isValid: false,
            error: "Please use a different email provider."
        }
    }

    // Additional pattern-based validation for suspicious domains
    const suspiciousPatterns = ["temp", "trash", "fake", "disposable", "throw", "spam"]
    if (suspiciousPatterns.some(pattern => domain.includes(pattern))) {
        return {
            isValid: false,
            error: "This email domain appears to be temporary. Please use a business email."
        }
    }

    return { isValid: true }
}

/**
 * Quick check if an email domain is consumer/personal
 */
export function isConsumerEmail(email: string): boolean {
    const domain = email.split("@")[1]?.toLowerCase()
    return domain ? CONSUMER_EMAIL_DOMAINS.includes(domain) : false
}

/**
 * Quick check if an email domain is disposable
 */
export function isDisposableEmail(email: string): boolean {
    const domain = email.split("@")[1]?.toLowerCase()
    return domain ? DISPOSABLE_EMAIL_DOMAINS.includes(domain) : false
}

/**
 * Quick check if an email domain is blocked
 */
export function isBlockedCompanyEmail(email: string): boolean {
    const domain = email.split("@")[1]?.toLowerCase()
    return domain ? BLOCKED_COMPANY_DOMAINS.some(blockedDomain => domain.includes(blockedDomain)) : false
}
